import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';

export const SidebarItem = ({ item, isCollapsed }) => {
  const location = useLocation();
  const currentFullUrl = location.pathname + location.search;
  const hasSubItems = item.subItems && item.subItems.length > 0;

  // Parent is active if its path matches OR if any child is active
  const isParentPathActive = location.pathname === item.path;
  const isAnyChildActive = hasSubItems && item.subItems.some(sub => currentFullUrl === sub.path);
  const isActive = isParentPathActive || isAnyChildActive;

  // Auto-expand if a child is active
  const [isOpen, setIsOpen] = useState(isAnyChildActive);

  useEffect(() => {
    if (isAnyChildActive) {
      setIsOpen(true);
    }
  }, [isAnyChildActive, currentFullUrl]);

  const toggleOpen = (e) => {
    if (hasSubItems) {
      e.preventDefault();
      setIsOpen(!isOpen);
    }
  };

  return (
    <div className="space-y-1">
      <Link
        to={hasSubItems ? '#' : item.path}
        onClick={toggleOpen}
        className={`flex items-center justify-between px-3 py-2.5 rounded-xl text-sm font-medium transition-all group ${
          isActive
            ? 'bg-indigo-600 text-white shadow-lg shadow-indigo-500/20'
            : 'text-slate-400 hover:text-slate-200 hover:bg-slate-800/50'
        }`}
      >
        <div className="flex items-center gap-3">
          <SafeIcon
            name={item.icon}
            className={`text-lg transition-colors ${isActive ? 'text-white' : 'text-slate-500 group-hover:text-indigo-400'}`}
          />
          {!isCollapsed && <span>{item.name}</span>}
        </div>
        {!isCollapsed && hasSubItems && (
          <SafeIcon
            name="ChevronRight"
            className={`text-xs transition-transform duration-200 ${isOpen ? 'rotate-90' : ''}`}
          />
        )}
      </Link>

      <AnimatePresence>
        {isOpen && !isCollapsed && hasSubItems && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="overflow-hidden pl-9 space-y-1 mt-1"
          >
            {item.subItems.map((sub, index) => {
              // Precise active check using full URL (path + search)
              const isSubActive = currentFullUrl === sub.path;

              return (
                <Link
                  key={`${sub.path}-${index}`}
                  to={sub.path}
                  className={`block py-2 text-xs transition-colors ${
                    isSubActive
                      ? 'text-white font-bold'
                      : 'text-slate-400 hover:text-slate-200'
                  }`}
                >
                  {sub.name}
                </Link>
              );
            })}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};